// frontend/src/contexts/AuthContext.tsx
import { createContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { api, setToken, getToken, clearToken, ApiError, UnauthorizedError, retryAfterOf } from '../lib/api';
import type { Account } from '../lib/types';

// 'login' = no session: the server declined an anonymous one (the per-IP rate
// limit, or the emergency REQUIRE_INVITE gate). The app renders NoSeat, which
// asks for nothing: the player UI has no code or email sign-in (2026-09-27).
type Status = 'loading' | 'ready' | 'error' | 'login';

export interface AuthValue {
  status: Status;
  token: string | null;
  account: Account | null;
  error: string | null;
  /**
   * With error ANON_RATE_LIMITED: the server's Retry-After in seconds, or
   * null when it sent none. NoSeat words the wait from it.
   */
  anonRetryAfter?: number | null;
  /**
   * F9: true when this session is an anonymous seat, false when it belongs to
   * a named account (restored from this browser's device cookie).
   * The account payload carries no such flag, and "no email" is not it: a
   * code invitee has a named account and no email.
   */
  anonymous?: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthValue | null>(null);

const INVITE_STORAGE_KEY = 'scotus.invite';

/**
 * Set as ``error`` alongside status 'login' when the server declined to open
 * an anonymous session because of its per-IP rate limit (429). NoSeat reads
 * it and says so plainly, instead of looking like a closed door.
 */
export const ANON_RATE_LIMITED = 'anon_rate_limited';

/**
 * Read a personalized invite token from the landing URL (`?invite=<token>`)
 * and stash it so it survives the brief window between page load and the
 * anon-auth call (and any client-side route change that drops the query).
 * Returns the token to use for this bootstrap, or null.
 */
function captureInviteToken(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('invite');
    if (fromUrl) {
      sessionStorage.setItem(INVITE_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(INVITE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearInviteToken(): void {
  try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch { /* ignore */ }
}

// Set by logout() for the rest of the tab session so the remembered-IP
// restore doesn't instantly sign the person straight back in (making the
// Log out button a no-op for code invitees).
const LOGGED_OUT_KEY = 'scotus.loggedout';

// How the stored bearer was obtained, kept beside it in localStorage (F9).
const SESSION_KIND_KEY = 'scotus.session_kind';
type SessionKind = 'anon' | 'named';

function readSessionKind(): SessionKind | null {
  try {
    const v = localStorage.getItem(SESSION_KIND_KEY);
    return v === 'anon' || v === 'named' ? v : null;
  } catch { return null; }
}

function writeSessionKind(kind: SessionKind | null): void {
  try {
    if (kind) localStorage.setItem(SESSION_KIND_KEY, kind);
    else localStorage.removeItem(SESSION_KIND_KEY);
  } catch { /* ignore */ }
}

function suppressIpRestore(): boolean {
  try { return sessionStorage.getItem(LOGGED_OUT_KEY) === '1'; } catch { return false; }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [account, setAccount] = useState<Account | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anonRetryAfter, setAnonRetryAfter] = useState<number | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const adopt = (tok: string, acct: Account, kind: SessionKind) => {
    setToken(tok);
    setTokenState(tok);
    setAccount(acct);
    writeSessionKind(kind);
    setAnonymous(kind === 'anon');
  };
  const bootstrapped = useRef(false);

  const bootstrap = async () => {
    setStatus('loading');
    setError(null);
    setAnonRetryAfter(null);
    try {
      if (getToken()) {
        try {
          const me = await api.getMe();
          setAccount(me.account);
          // A bearer stored before the kind was recorded: fall back to the
          // old reading (no email), which is right for everyone but a code
          // invitee, and that session records 'named' at its next sign-in.
          const kind = readSessionKind();
          setAnonymous(kind ? kind === 'anon' : !me.account.email);
          setTokenState(getToken());
          setStatus('ready');
          return;
        } catch (err) {
          if (!(err instanceof UnauthorizedError)) throw err;
          // stale token, fall through to anon
          clearToken();
          writeSessionKind(null);
          setTokenState(null);
        }
      }
      // No valid token. First ask whether this browser is one the server
      // already knows (the httpOnly device cookie set at a previous sign-in):
      // "never log in again". This has to come BEFORE any anonymous session.
      // It used to run last, after the open-door postAnon, and on the open
      // site that call always succeeds, so restore never ran and every
      // returning player whose bearer was gone got a fresh empty account
      // (F2). Suppressed for the tab session after an explicit logout.
      const invite = captureInviteToken();
      if (!invite && !suppressIpRestore()) {
        // A first visit with no invite: restore, or else the anonymous seat,
        // in one round trip (FEEDBACK_1: two serial requests here held up the
        // whole site on a slow phone). A server without the flag answers 204,
        // and the ordinary path below runs.
        try {
          const auth = await api.postIpRestoreOrAnon();
          if (auth) {
            adopt(auth.token, auth.account, auth.session_kind === 'anon' ? 'anon' : 'named');
            setStatus('ready');
            return;
          }
        } catch (err) {
          if (err instanceof ApiError && (err.status === 403 || err.status === 429)) {
            // The same refusals the open door gives; NoSeat explains.
            if (err.status === 429) {
              setAnonRetryAfter(retryAfterOf(err));
              setError(ANON_RATE_LIMITED);
            }
            setStatus('login');
            return;
          }
          /* anything else: fall through to the two-step path */
        }
      }
      if (invite && !suppressIpRestore()) {
        try {
          const auth = await api.postIpRestore();
          if (auth) {
            // A remembered player has an account already; an invite in the
            // link has nothing left to do.
            clearInviteToken();
            adopt(auth.token, auth.account, 'named');
            setStatus('ready');
            return;
          }
        } catch {
          /* not a remembered browser, carry on to invite / anon */
        }
      }
      // If the link carried a personalized invite, redeem it for an
      // anonymous session (and bind attribution). No code is typed.
      if (invite) {
        try {
          const auth = await api.postAnon(invite);
          clearInviteToken();
          adopt(auth.token, auth.account, 'anon');
          setStatus('ready');
          return;
        } catch (err) {
          clearInviteToken();
          if (!(err instanceof ApiError)) throw err;
          // Any refusal, including a single-use open invite answering
          // `invite_email_required` (claiming it needs an email code, and
          // the player UI has none): drop the token and open an anonymous
          // session like any other visitor.
        }
      }
      // The open door: a plain anonymous session, which is how every visitor
      // gets in. Only the emergency REQUIRE_INVITE gate (403) or the per-IP
      // rate limit (429) refuses it, and then NoSeat says why.
      let anonRateLimited = false;
      try {
        const auth = await api.postAnon();
        adopt(auth.token, auth.account, 'anon');
        setStatus('ready');
        return;
      } catch (err) {
        /* gated server or rate limited, NoSeat explains */
        anonRateLimited = err instanceof ApiError && err.status === 429;
        if (anonRateLimited) setAnonRetryAfter(retryAfterOf(err));
      }
      if (anonRateLimited) setError(ANON_RATE_LIMITED);
      setStatus('login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'auth failed');
      setStatus('error');
    }
  };

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    void bootstrap();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    const me = await api.getMe();
    setAccount(me.account);
  };

  const logout = async () => {
    try { await api.logout(); } catch { /* ignore */ }
    try { sessionStorage.setItem(LOGGED_OUT_KEY, '1'); } catch { /* ignore */ }
    clearToken();
    writeSessionKind(null);
    setTokenState(null);
    setAccount(null);
    setAnonymous(false);
    bootstrapped.current = false;
    await bootstrap();
  };

  return (
    <AuthContext.Provider
      value={{ status, token, account, error, anonRetryAfter, anonymous, refresh, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
